import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from "react";
import {
  ArrowLeft,
  X,
  Play,
  Volume2,
  VolumeX,
  LoaderCircle,
  RotateCcw,
  Maximize,
} from "lucide-react";
import type { VideoItem } from "./data";
export function IconButton({
  label,
  children,
  onClick,
  className = "",
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-button ${className}`}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
export function Header({
  title,
  onBack,
  right,
  style,
}: {
  title: string;
  onBack: () => void;
  right?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <header className="page-header" style={style}>
      <IconButton label="返回" onClick={onBack}>
        <ArrowLeft />
      </IconButton>
      <h1>{title}</h1>
      <div className="header-right">{right}</div>
    </header>
  );
}
export function Empty({
  title,
  text,
  children,
}: {
  title: string;
  text: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-symbol">
        <Play size={25} />
      </div>
      <h2>{title}</h2>
      <p>{text}</p>
      {children}
    </div>
  );
}
export function Modal({
  title,
  onClose,
  children,
  drawer = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  drawer?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const touch = useRef(0);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key === "Tab") {
        const focusables = ref.current?.querySelectorAll<HTMLElement>(
          'button, input, textarea, select, [tabindex="0"]',
        );
        if (!focusables?.length) return;
        const first = focusables[0],
          last = focusables[focusables.length - 1];
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === ref.current)
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === ref.current)
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previous?.focus({ preventScroll: true });
    };
  }, []);
  return (
    <div className={`modal-layer ${drawer ? "drawer-layer" : ""}`}>
      <button className="scrim" aria-label="关闭遮罩" onClick={onClose} />
      <div
        className={drawer ? "drawer" : "sheet"}
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onTouchStart={(e) => {
          touch.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (drawer && e.changedTouches[0].clientX - touch.current < -80)
            onClose();
        }}
      >
        {!drawer && <div className="sheet-handle" />}
        <header className="sheet-header">
          <h2>{title}</h2>
          <IconButton label="关闭" onClick={onClose}>
            <X size={21} />
          </IconButton>
        </header>
        {children}
      </div>
    </div>
  );
}
export function VideoPlayer({
  video,
  active,
  autoplay,
  muted,
  onMute,
  onNext,
  onView,
}: {
  video: VideoItem;
  active: boolean;
  autoplay: boolean;
  muted: boolean;
  onMute: (muted: boolean) => void;
  onNext: () => void;
  onView: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState(0);
  const seeking = useRef(false);
  const [appActive, setAppActive] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [aspect, setAspect] = useState(0);
  const canPlay = active && appActive;
  const desiredPlay = useRef(false);
  const userPaused = useRef(false);
  const sourceEpoch = useRef(0);
  const sound = useRef({ muted, onMute });
  sound.current = { muted, onMute };
  desiredPlay.current = canPlay && autoplay && !userPaused.current;
  async function startPlayback(el: HTMLVideoElement) {
    if (!desiredPlay.current) return;
    const epoch = sourceEpoch.current;
    try {
      await el.play();
    } catch (error) {
      if (
        !desiredPlay.current ||
        ref.current !== el ||
        epoch !== sourceEpoch.current
      )
        return;
      // A new media element may need a fresh gesture for audible playback on Safari.
      if (
        error instanceof DOMException &&
        error.name === "NotAllowedError" &&
        !el.muted
      ) {
        el.muted = true;
        el.defaultMuted = true;
        sound.current.onMute(true);
        try {
          await el.play();
          return;
        } catch {
          /* Browser-level autoplay restrictions. */
        }
      }
      setPlaying(false);
      setLoading(false);
    }
  }
  useEffect(() => {
    const visibility = () => setAppActive(!document.hidden);
    const native = (e: Event) =>
      setAppActive((e as CustomEvent<boolean>).detail);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("native-active", native);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("native-active", native);
    };
  }, []);
  useEffect(() => {
    userPaused.current = false;
    desiredPlay.current = canPlay && autoplay;
    setFailed(false);
    setProgress(0);
    seeking.current = false;
    setAspect(0);
    setPlaying(false);
    setLoading(!!video.src && canPlay && autoplay);
  }, [video.id, video.src]);
  useEffect(() => {
    const el = ref.current;
    if (!el || !video.src) return;
    sourceEpoch.current += 1;
    let cancelled = false;
    let destroy: (() => void) | undefined;
    // Set inline and mute policies before attaching any MP4/HLS source.
    el.playsInline = true;
    el.setAttribute("webkit-playsinline", "");
    el.setAttribute("x5-playsinline", "true");
    el.muted = sound.current.muted;
    el.defaultMuted = sound.current.muted;
    el.autoplay = desiredPlay.current;
    const start = () => {
      if (!cancelled) void startPlayback(el);
    };
    el.addEventListener("canplay", start);
    const appleWebKit =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Safari/.test(navigator.userAgent) &&
        !/Chrome|Chromium|Android/.test(navigator.userAgent));
    if (
      /\.m3u8(?:$|\?)/i.test(video.src) &&
      !(appleWebKit && el.canPlayType("application/vnd.apple.mpegurl"))
    ) {
      void import("hls.js/light")
        .then(({ default: Hls }) => {
          if (cancelled) return;
          if (!Hls.isSupported()) {
            setFailed(true);
            return;
          }
          const hls = new Hls({
            enableWorker: true,
            maxBufferLength: 20,
            backBufferLength: 10,
            maxMaxBufferLength: 40,
          });
          destroy = () => hls.destroy();
          let recovered = false;
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (!data.fatal) return;
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered) {
              recovered = true;
              hls.recoverMediaError();
            } else {
              setFailed(true);
              setLoading(false);
              hls.stopLoad();
            }
          });
          hls.on(Hls.Events.MANIFEST_PARSED, start);
          hls.loadSource(video.src!);
          hls.attachMedia(el);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    } else {
      el.src = video.src;
      el.addEventListener("loadedmetadata", start);
    }
    return () => {
      cancelled = true;
      sourceEpoch.current += 1;
      destroy?.();
      el.removeEventListener("loadedmetadata", start);
      el.removeEventListener("canplay", start);
      el.pause();
      el.removeAttribute("src");
      el.load();
    };
  }, [video.src, video.id, attempt]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!canPlay) el.pause();
    else if (desiredPlay.current && el.readyState >= 1) void startPlayback(el);
  }, [canPlay, autoplay, video.id, video.src]);
  async function play() {
    const el = ref.current;
    if (!el) return;
    if (!el.paused) {
      userPaused.current = true;
      desiredPlay.current = false;
      el.autoplay = false;
      el.pause();
    } else {
      userPaused.current = false;
      desiredPlay.current = canPlay && autoplay;
      setLoading(true);
      try {
        await el.play();
        onView();
      } catch {
        setLoading(false);
        setFailed(true);
      }
    }
  }
  function seek(value: number) {
    if (
      ref.current &&
      Number.isFinite(ref.current.duration) &&
      ref.current.duration > 0
    ) {
      setProgress(value);
      ref.current.currentTime = (value / 100) * ref.current.duration;
    }
  }
  function seekAt(clientX: number, control: HTMLDivElement) {
    const rect = control.getBoundingClientRect();
    const value =
      ((clientX - rect.left - 7) / Math.max(1, rect.width - 14)) * 100;
    seek(Math.min(100, Math.max(0, value)));
  }
  function updateAspect(el: HTMLVideoElement) {
    if (el.videoWidth && el.videoHeight)
      setAspect(el.videoWidth / el.videoHeight);
  }
  return (
    <div
      className={`video-stage ${video.src ? "playable" : ""} ${aspect > 1 ? "is-landscape" : ""}`}
    >
      <img className="video-poster" src={video.cover} alt="" />
      {video.src && (
        <video
          key={video.id}
          ref={ref}
          poster={video.cover}
          playsInline
          autoPlay={desiredPlay.current}
          muted={muted}
          preload="metadata"
          style={aspect > 1 ? { aspectRatio: String(aspect) } : undefined}
          aria-label={video.title}
          onLoadedMetadata={(event) => updateAspect(event.currentTarget)}
          // Native HLS can expose its dimensions after loadedmetadata, or change
          // them when switching rendition. Keep both browser paths in sync.
          onResize={(event) => updateAspect(event.currentTarget)}
          onCanPlay={(event) => updateAspect(event.currentTarget)}
          onPlay={() => {
            setPlaying(true);
            onView();
          }}
          onPause={() => setPlaying(false)}
          onWaiting={() => setLoading(true)}
          onPlaying={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setFailed(true);
          }}
          onEnded={() => {
            setPlaying(false);
            if (autoplay) onNext();
          }}
          onTimeUpdate={() => {
            const el = ref.current;
            if (el?.duration && !seeking.current)
              setProgress((el.currentTime / el.duration) * 100);
          }}
        />
      )}
      <div className="video-scrim" />
      {video.src ? (
        <button
          className="video-tap"
          aria-label={playing ? "暂停视频" : "播放视频"}
          onClick={play}
        >
          {!playing && !loading && !failed && (
            <Play className="center-play" size={52} fill="currentColor" />
          )}
        </button>
      ) : (
        <div className="cover-badge">
          {video.remote ? "分集加载中或暂不可播放" : "概念封面预览"}
        </div>
      )}
      {loading && <LoaderCircle className="player-loading spin" />}
      {failed && (
        <button
          className="player-error"
          onClick={() => {
            userPaused.current = false;
            setFailed(false);
            setAttempt((s) => s + 1);
          }}
        >
          <RotateCcw size={18} /> 播放失败，点击重试
        </button>
      )}
      {video.src && (
        <div className="player-controls">
          <IconButton
            label={muted ? "开启声音" : "静音"}
            onClick={() => {
              const nextMuted = !muted;
              if (ref.current) {
                // Updating in the tap handler preserves the user's audio gesture on iOS.
                ref.current.muted = nextMuted;
                ref.current.defaultMuted = nextMuted;
              }
              onMute(nextMuted);
            }}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </IconButton>
          <span>
            {video.remote
              ? `第${video.episode}集 · HLS`
              : video.demo
                ? "演示视频"
                : "本地视频"}
          </span>
          <IconButton
            label="全屏播放"
            onClick={() => {
              const el = ref.current as HTMLVideoElement & {
                webkitEnterFullscreen?: () => void;
              };
              if (el.requestFullscreen)
                void el
                  .requestFullscreen()
                  .catch(() => el.webkitEnterFullscreen?.());
              else el.webkitEnterFullscreen?.();
            }}
          >
            <Maximize size={18} />
          </IconButton>
        </div>
      )}
      {video.src && (
        <div
          className="video-progress-control"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            seeking.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            event.currentTarget
              .querySelector("input")
              ?.focus({ preventScroll: true });
            seekAt(event.clientX, event.currentTarget);
          }}
          onPointerMove={(event) => {
            if (
              seeking.current &&
              event.currentTarget.hasPointerCapture(event.pointerId)
            ) {
              event.stopPropagation();
              seekAt(event.clientX, event.currentTarget);
            }
          }}
          onPointerUp={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              seekAt(event.clientX, event.currentTarget);
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
            seeking.current = false;
            event.stopPropagation();
          }}
          onLostPointerCapture={() => {
            seeking.current = false;
          }}
          onPointerCancel={(event) => {
            seeking.current = false;
            event.stopPropagation();
          }}
          onTouchStart={(event) => {
            seeking.current = true;
            event.stopPropagation();
          }}
          onTouchEnd={(event) => {
            seeking.current = false;
            event.stopPropagation();
          }}
          onTouchCancel={(event) => {
            seeking.current = false;
            event.stopPropagation();
          }}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <input
            className="video-progress"
            aria-label="播放进度"
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={progress}
            onChange={(event) => seek(Number(event.target.value))}
            style={{
              backgroundImage: `linear-gradient(to right, #ff395c ${progress}%, #ffffff40 ${progress}%)`,
            }}
          />
        </div>
      )}
    </div>
  );
}
